import { Controller } from "@hotwired/stimulus";

const MAX_DATE = "2099-12-31";
const MAX_TIME = "23:59";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export default class extends Controller {
  static targets = ["typology", "duration", "validUntilDate", "validUntilTime"];

  connect() {
    if (!this.durationTarget.dataset.defaultMax) {
      this.durationTarget.dataset.defaultMax = this.durationTarget.max;
    }

    if (!this.validUntilDateTarget.dataset.defaultMax) {
      this.validUntilDateTarget.dataset.defaultMax = this.validUntilDateTarget.max;
    }

    this.handleTimeChange = this.timeChanged.bind(this);
    this.handleTimeInput = this.timeChanged.bind(this);
    this.validUntilTimeTarget?.addEventListener("change", this.handleTimeChange);
    this.validUntilTimeTarget?.addEventListener("input", this.handleTimeInput);
    this.initializeFields();
  }

  disconnect() {
    this.validUntilTimeTarget?.removeEventListener("change", this.handleTimeChange);
    this.validUntilTimeTarget?.removeEventListener("input", this.handleTimeInput);
  }

  typologyChanged() {
    this.initializeFields();
  }

  durationChanged() {
    if (!this.hasDurationTarget || !this.hasValidUntilDateTarget || !this.hasValidUntilTimeTarget) return;

    if (!this.selectedPromoTypology) {
      this.setDefaultTypologyValues();
      return;
    }

    const duration = this.clampedDurationValue();
    if (!Number.isFinite(duration) || duration <= 0) return;

    const expiration = this.expirationForDuration(duration);
    if (!expiration) return;

    this.validUntilDateTarget.value = this.formatDate(expiration);
    this.validUntilTimeTarget.value = this.formatTime(expiration);
  }

  dateChanged() {
    this.expirationChanged();
  }

  timeChanged() {
    this.expirationChanged();
  }

  initializeFields() {
    if (!this.hasTypologyTarget || !this.hasDurationTarget || !this.hasValidUntilDateTarget || !this.hasValidUntilTimeTarget) return;

    if (this.selectedPromoTypology) {
      this.setPromoValues();
    } else {
      this.setDefaultTypologyValues();
    }
  }

  setPromoValues() {
    this.durationTarget.readOnly = false;
    this.validUntilDateTarget.readOnly = !this.expirationEditable;
    this.validUntilTimeTarget.readOnly = !this.expirationEditable;

    const maxDuration = this.allowedPromoDuration;
    if (maxDuration) this.durationTarget.max = `${maxDuration}`;
    if (maxDuration) this.validUntilDateTarget.max = this.formatDate(this.expirationLimitForDuration(maxDuration));

    if (this.withExpiration) {
      this.durationTarget.value = this.clampedDurationValue(this.fieldValue || this.licenceDuration);
    } else if (!this.durationTarget.value || Number(this.durationTarget.value) > maxDuration) {
      this.durationTarget.value = this.clampedDurationValue(this.fieldValue || this.defaultPromoDuration);
    }

    this.durationChanged();
  }

  setDefaultTypologyValues() {
    this.durationTarget.readOnly = true;
    this.validUntilDateTarget.readOnly = true;
    this.validUntilTimeTarget.readOnly = true;

    const maxDuration = this.defaultDurationLimit;
    if (maxDuration) this.durationTarget.max = `${maxDuration}`;
    this.validUntilDateTarget.max = this.validUntilDateTarget.dataset.defaultMax;

    if (this.withExpiration) {
      this.durationTarget.value = this.licenceDuration;
      this.validUntilDateTarget.value = this.formatDate(this.defaultExpirationDateTime);
    } else {
      this.durationTarget.value = this.unlimitedDuration;
      this.validUntilDateTarget.value = MAX_DATE;
    }

    this.validUntilTimeTarget.value = MAX_TIME;
  }

  expirationChanged() {
    if (!this.hasDurationTarget || !this.hasValidUntilDateTarget || !this.hasValidUntilTimeTarget) return;

    if (!this.selectedPromoTypology) {
      this.setDefaultTypologyValues();
      return;
    }

    let selectedDateTime = this.selectedExpirationDateTime();
    if (!selectedDateTime) return;

    const maxExpiration = this.maximumExpirationDateTime;
    if (maxExpiration && selectedDateTime > maxExpiration) {
      selectedDateTime = maxExpiration;
      this.validUntilDateTarget.value = this.formatDate(maxExpiration);
      this.validUntilTimeTarget.value = this.formatTime(maxExpiration);
    }

    const baseline = this.durationBaseline();
    const diff = Math.ceil((selectedDateTime - baseline) / MS_PER_DAY);
    this.durationTarget.value = this.clampedDurationValue(Math.max(diff, 1));
  }

  startOfDayPlusDays(days) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + Number(days || 0));
    return date;
  }

  endOfDayPlusDays(days) {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    date.setDate(date.getDate() + Number(days || 0));
    return date;
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  formatTime(date) {
    const hours = `${date.getHours()}`.padStart(2, "0");
    const minutes = `${date.getMinutes()}`.padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  parseDate(value) {
    if (!value) return null;

    const htmlDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (htmlDate) {
      const [, year, month, day] = htmlDate;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  selectedExpirationDateTime() {
    const selectedDate = this.parseDate(this.validUntilDateTarget.value);
    if (!selectedDate) return null;

    const [hours, minutes] = (this.validUntilTimeTarget.value || MAX_TIME).split(":").map(Number);
    selectedDate.setHours(hours || 0, minutes || 0, 0, 0);
    return selectedDate;
  }

  durationBaseline() {
    const createdAt = this.validUntilDateTarget.dataset.createdAt;
    if (this.useCreatedAt && createdAt) {
      const baseDate = this.parseDate(createdAt);
      if (baseDate) {
        baseDate.setHours(0, 0, 0, 0);
        return baseDate;
      }
    }

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    return todayEnd;
  }

  expirationForDuration(duration) {
    const maxDuration = this.allowedPromoDuration;
    const maxExpiration = this.maximumExpirationDateTime;
    if (maxDuration && maxExpiration && duration >= maxDuration && maxDuration >= this.defaultDurationLimit) return maxExpiration;

    return this.expirationLimitForDuration(duration);
  }

  expirationLimitForDuration(duration) {
    const createdAt = this.validUntilDateTarget.dataset.createdAt;
    if (this.useCreatedAt && createdAt) {
      const baseDate = this.parseDate(createdAt);
      if (!baseDate) return null;
      baseDate.setDate(baseDate.getDate() + duration);
      baseDate.setHours(23, 59, 0, 0);
      return baseDate;
    }

    return this.endOfDayPlusDays(duration);
  }

  clampedDurationValue(value = this.durationTarget.value) {
    const duration = Number(value);
    if (!Number.isFinite(duration) || duration <= 0) return duration;

    const maxDuration = this.allowedPromoDuration;
    const nextDuration = maxDuration ? Math.min(duration, maxDuration) : duration;
    this.durationTarget.value = nextDuration;
    return nextDuration;
  }

  get selectedPromoTypology() {
    return this.typologyTarget.value === this.typologyTarget.dataset.selected;
  }

  get withExpiration() {
    return this.durationTarget.dataset.withExpiration === "true";
  }

  get useCreatedAt() {
    return this.durationTarget.dataset.useCreatedAt === "true";
  }

  get licenceDuration() {
    return Number(this.durationTarget.dataset.duration || 0);
  }

  get fieldValue() {
    return Number(this.durationTarget.dataset.fieldValue || 0);
  }

  get defaultPromoDuration() {
    return this.durationTarget.dataset.wl === "samir" ? 90 : 180;
  }

  get allowedPromoDuration() {
    const promoAttribute = Number(this.durationTarget.dataset.promotionalDurationLimit);
    if (Number.isFinite(promoAttribute) && promoAttribute > 0) return promoAttribute;

    const maxAttribute = Number(this.durationTarget.max);
    if (Number.isFinite(maxAttribute) && maxAttribute > 0) return maxAttribute;

    if (!this.withExpiration) return this.defaultDurationLimit;

    return this.withExpiration && this.licenceDuration > 0 ? this.licenceDuration : null;
  }

  get maximumExpirationDateTime() {
    const maxDate = this.parseDate(this.validUntilDateTarget.max);
    if (!maxDate) return null;

    maxDate.setHours(23, 59, 0, 0);
    return maxDate;
  }

  get defaultExpirationDateTime() {
    return this.maximumExpirationDateTime || this.endOfDayPlusDays(this.licenceDuration);
  }

  get unlimitedDuration() {
    return this.defaultDurationLimit || this.fieldValue || this.defaultPromoDuration;
  }

  get defaultDurationLimit() {
    const maxAttribute = Number(this.durationTarget.dataset.defaultMax);
    return Number.isFinite(maxAttribute) && maxAttribute > 0 ? maxAttribute : null;
  }

  get expirationEditable() {
    return this.validUntilDateTarget.dataset.expirationEditable === "true";
  }
}
